// Need to use the React-specific entry point to import createApi
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'

import { openSnackbar } from './notify';
import { setAuthUser } from '../reducers/authUser';
import { displayBackdrop, hideBackdrop } from './loading';

const API_URL = process.env.REACT_APP_API_URL;

// Define a service using a base URL and expected endpoints
export const userService = createApi({
    reducerPath: 'userService',
    baseQuery: fetchBaseQuery({ baseUrl: API_URL }),
    tagTypes: ['AUTHUSER', 'USER'],
    endpoints: (builder) => ({
        getAuthUser: builder.query({
            query: () => `me`,
            providesTags: ['AUTHUSER'],
            async onQueryStarted(arg, { dispatch, queryFulfilled }) {
                const { data } = await queryFulfilled;
                const authData = data?.data;
                if(authData && authData?.user_id){
                    const genAuthData = { userID: authData?.user_id, lastLogin: authData?.last_login, userEmail: authData?.user_email, displayName: authData?.display_name };
                    dispatch(setAuthUser(genAuthData));
                }else{
                    dispatch(setAuthUser({ userID: "", lastLogin: "", userEmail: "", displayName: "" }));
                }
            },

        }),
        login: builder.mutation({
            query: (initialPost) => ({ url: '/login', method: 'POST', body: initialPost }),
            invalidatesTags: ['AUTHUSER']
        }),
        logout: builder.query({
            query: () => `logout`,
        }),
        getUserList: builder.query({
            query: () => `user`,
            providesTags: [{ type: 'USER', id: 'all-user' }],
            async onQueryStarted(arg, { dispatch, queryFulfilled }) {
                dispatch(displayBackdrop());
                try {
                    const { data } = await queryFulfilled;
                    if(data){
                        dispatch(hideBackdrop());
                    }
                } catch (err) {
                    const { error } = err;
                    dispatch(hideBackdrop());
                    dispatch(openSnackbar({ options: { variant: "error" }, message: error?.data?.message || 'Something went wrong, Please try again.' }));
                }
            },
            transformResponse: (response) => response.data,
        }),
        getUserObj: builder.query({
            query: (userID) => `user/${userID}`,
            providesTags: (result, error, arg) => [{ type: 'USER', id: arg }],
            async onQueryStarted(arg, { dispatch, queryFulfilled }) {
                dispatch(displayBackdrop());
                try {
                    const { data } = await queryFulfilled;
                    if(data){
                        dispatch(hideBackdrop());
                    }
                } catch (err) {
                    const { error } = err;
                    dispatch(hideBackdrop());
                    dispatch(openSnackbar({ options: { variant: "error" }, message: error?.data?.message || 'Something went wrong, Please try again.' }));
                }
            },
            transformResponse: (response) => response.data,
        }),
        createUser: builder.mutation({
            query: (initialPost) => ({ url: 'user', method: 'POST', body: initialPost }),
            invalidatesTags: [{ type: 'USER', id: 'all-user' }],
            async onQueryStarted(arg, { dispatch, queryFulfilled }) {
                dispatch(displayBackdrop());
                try {
                    const { data } = await queryFulfilled;
                    if(data){
                        dispatch(hideBackdrop());
                    }
                } catch (err) {
                    const { error } = err;
                    dispatch(hideBackdrop());
                    dispatch(openSnackbar({ options: { variant: "error" }, message: error?.data?.message || 'Something went wrong, Please try again.' }));
                }
            },
        }),
        updateUser: builder.mutation({
            query: ({userID, initialPost}) => ({ url: `user/${userID}`, method: 'PUT', body: initialPost }),
            invalidatesTags: (result, error, arg) => [{ type: 'USER', id: arg.userID }, { type: 'USER', id: 'all-user' }],
            async onQueryStarted(arg, { dispatch, queryFulfilled }) {
                dispatch(displayBackdrop());
                try {
                    const { data } = await queryFulfilled;
                    if(data){
                        dispatch(hideBackdrop());
                    }
                } catch (err) {
                    const { error } = err;
                    dispatch(hideBackdrop());
                    dispatch(openSnackbar({ options: { variant: "error" }, message: error?.data?.message || 'Something went wrong, Please try again.' }));
                }
            },
        }),
        deleteUser: builder.mutation({
            query: (initialPost) => ({ url: `user`, method: 'DELETE', body: initialPost }),
            invalidatesTags: (result, error, arg) => [{ type: 'USER', id: 'all-user' }],
            async onQueryStarted(arg, { dispatch, queryFulfilled }) {
                dispatch(displayBackdrop());
                try {
                    const { data } = await queryFulfilled;
                    if(data){
                        dispatch(hideBackdrop());
                    }
                } catch (err) {
                    const { error } = err;
                    dispatch(hideBackdrop());
                    dispatch(openSnackbar({ options: { variant: "error" }, message: error?.data?.message || 'Something went wrong, Please try again.' }));
                }
            },
        }),
        forgotPassword: builder.mutation({
            query: (initialPost) => ({ url: 'forgot-password', method: 'POST', body: initialPost }),
            async onQueryStarted(arg, { dispatch, queryFulfilled }) {
                dispatch(displayBackdrop());
                try {
                    const { data } = await queryFulfilled;
                    if(data){
                        dispatch(hideBackdrop());
                    }
                } catch (err) {
                    const { error } = err;
                    dispatch(hideBackdrop());
                    dispatch(openSnackbar({ options: { variant: "error" }, message: error?.data?.message || 'Something went wrong, Please try again.' }));
                }
            },
        }),
        resetPassword: builder.mutation({
            query: ({resetToken, initialPost}) => ({ url: `forgot-password/${resetToken}`, method: 'POST', body: initialPost }),
            async onQueryStarted(arg, { dispatch, queryFulfilled }) {
                dispatch(displayBackdrop());
                try {
                    const { data } = await queryFulfilled;
                    if(data){
                        dispatch(hideBackdrop());
                    }
                } catch (err) {
                    const { error } = err;
                    dispatch(hideBackdrop());
                    dispatch(openSnackbar({ options: { variant: "error" }, message: error?.data?.message || 'Something went wrong, Please try again.' }));
                }
            },
        }),
    }),
})

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const { useGetAuthUserQuery, useLoginMutation, useLogoutQuery, useGetUserListQuery, useGetUserObjQuery, useCreateUserMutation, useUpdateUserMutation, useDeleteUserMutation, useForgotPasswordMutation, useResetPasswordMutation } = userService;